<template>
  <v-row v-scroll="onScroll">
    <!-- header -->
    <mini-header :breadcrumbs="breadcrumbs" :dense="true" :hiddenTab="true" :scroll="scroll">
      <template v-slot:main>
        <v-text-field
          v-model="search"
          outlined
          single-line
          dense
          hide-details
          placeholder="Escribe un RUT o razón social"
          clearable
          style="max-width: 650px; min-width: 550px;"
          @input="handleSearch"
          @click:clear="handleClear"
        >
          <template v-slot:prepend-inner>
            <v-icon style="color: var(--light-grey-500) !important;" size="24">mdi-magnify</v-icon>
            <v-divider vertical style="height: 16px; margin: 0 6px; margin-top:5px; border-color: var(--light-grey-50) !important;" />
          </template>
        </v-text-field>
      </template>
    </mini-header>
    <!-- end header -->

    <v-col cols="12" class="px-0 mt-20">
      <skeleton-list v-if="loading" />
      <template v-else>
        <div v-if="!suppliersList.length" class="d-flex justify-center align-center" style="width: 100%; min-height: calc(100vh - 300px);">
          <v-empty-state type="suppliers" id="document" :height="180" :hiddenDescription="true" customClass="mt-6" :isFree="true" title="No existen proveedores para mostrar" />
        </div>
        <v-simple-table v-else :height="heightTable" fixed-header :class="[{'fixed-table' : $store.state.base.isExpandNavigationDrawer}]">
          <template v-slot:default>
            <VTableHeaders :headers="headers" @sort="setSort" />
            <v-divider style="position: absolute; margin-top: -6px; margin-left: -5px; z-index: 2; min-width: 100%" />
            <tbody>
              <tr
                v-for="(item, i) in suppliersList"
                :key="i"
                v-intersect="(i + 1) === suppliersList?.length ? onLoadMore : ''"
                @mouseenter="hoveredRow = i"
                @mouseleave="handleRowLeave(item)"
              >
                <td class="body-2">
                  <template v-if="item.name || item.tax_id">
                    <span class="body-1 d-inline-block text-truncate" style="max-width: 95%;" v-if="item.name">{{ item.name }}</span>
                    <span class="d-block body-2 text--disabled font-italic" v-else>Sin especificar</span>
                    <span class="mt-n1 caption grey-300--text d-block" v-if="item.tax_id">{{$t('generals.RUT')}} {{item.tax_id}}</span>
                  </template>
                  <span class="body-2 text--disabled font-italic" v-else>Sin especificar</span>
                </td>
                <td class="body-2">
                  <span class="d-inline-block text-truncate" style="max-width: 96%;" v-if="item.tax_settings?.cl_activity_description || item.business_activity">{{item.tax_settings?.cl_activity_description || item.business_activity}}</span>
                  <span class="body-2 text--disabled" v-else>Sin especificar</span>
                </td>
                <td class="body-2 text-end">{{item.created | dateTimeSecond}}</td>
                <td class="text-end" style="width: 180px;">
                  <v-btn
                    v-show="hoveredRow === i || confirmAdd === item.id || successAdd === item.id"
                    :color="getButtonColor(item.id)"
                    small
                    @click.stop="handleButtonClick(item)"
                    :loading="loadingAdd === item.id"
                    :disabled="successAdd === item.id"
                  >
                    {{ getButtonText(item.id) }}
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </template>
    </v-col>

    <!-- Snackbar personalizado -->
    <v-snackbar
      v-model="showSnackbar"
      :timeout="3000"
      color="#222835"
      top
      min-width="50%"
    >
      <span class="white--text">{{ snackbarSupplier?.name || 'Nombre del proveedor' }} agregado a la categoría {{ categoryName || 'Nombre de la categoría' }} con éxito</span>
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          class="white--text mr-4"
          @click="handleUndo"
        >
          Deshacer
        </v-btn>
      </template>
    </v-snackbar>
  </v-row>
</template>

<script>
import { mapState } from 'vuex'
import MiniHeader from '@/components/commons/MiniHeader'
import SkeletonList from '@/components/commons/skeletonLoader/SkeletonList'
import VTableHeaders from '@/components/VTable/VTableHeaders'
import VEmptyState from '@/components/commons/VEmptyState'
import ListViewMixin from '@/mixins/ListViewMixin'
import GenericViewMixin from '@/mixins/GenericViewMixin'

export default {
  components: {
    MiniHeader,
    SkeletonList,
    VTableHeaders,
    VEmptyState
  },
  mixins: [
    GenericViewMixin,
    ListViewMixin
  ],
  data: () => ({
    defaultSegmentId: 'se_AAAAAAAAAAAAAAAA',
    search: '',
    searchTimeout: null,
    loading: false,
    loadingAdd: null,
    confirmAdd: null,
    successAdd: null,
    hoveredRow: null,
    scroll: 0,
    nextToken: null,
    categoryName: '',
    showSnackbar: false,
    snackbarSupplier: null,
    snackbarTimeout: null,
    headers: [
      { text: 'Proveedor', value: 'name', class: 'font-weight-bold', sortable: true },
      { text: 'Actividad económica', value: 'tax_settings', sortable: true },
      { text: 'Fecha de creación', value: 'created', align: 'end', sortable: true },
      { text: '', value: 'actions', align: 'end', sortable: false }
    ],
    breadcrumbs: {
      main: 'Recepción',
      children: [
        {
          text: 'Categorías',
          route: 'SuppliersCategoriesList'
        },
        {
          text1: ''
        },
        {
          text2: 'Agregar proveedores'
        }
      ]
    }
  }),
  computed: {
    ...mapState({
      suppliersList: state => state.suppliers.suppliersList
    }),
    heightTable () {
      // Si hay más datos por cargar (nextToken), siempre usar altura fija para que funcione el scroll
      if (this.nextToken) return 'calc(100vh - 275px)'
      // Si no hay más datos y hay pocos registros, adaptar al contenido
      if (this.suppliersList.length < 15) return 'auto'
      return 'calc(100vh - 275px)'
    }
  },
  created () {
    this.getList()
    this.getCategoryName()
  },
  methods: {
    onScroll (e) {
      this.scroll = e.target?.scrollingElement?.scrollTop ?? 0
    },
    handleSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.getList()
      }, 500)
    },
    handleClear () {
      this.search = ''
      this.getList()
    },
    getCategoryName () {
      this.$store.dispatch('suppliers/RETRIEVE', {
        resource: 'segments',
        id: this.$route.params.id
      })
      .then((response) => {
        const isDefault = this.$route.params.id === this.defaultSegmentId
        this.categoryName = isDefault ? 'Sin categoría' : (response.data?.name ?? 'Sin categoría')
        this.breadcrumbs.children[1].text1 = this.categoryName
        this.breadcrumbs.children[1].route1 = { name: 'SuppliersCategoriesRetrieveUpdate', params: { id: this.$route.params.id } }
      })
      .catch(() => {
        const isDefault = this.$route.params.id === this.defaultSegmentId
        this.breadcrumbs.children[1].text1 = isDefault ? 'Sin categoría' : 'Sin categoría'
        this.breadcrumbs.children[1].route1 = { name: 'SuppliersCategoriesRetrieveUpdate', params: { id: this.$route.params.id } }
      })
    },
    getList () {
      this.loading = true
      this.nextToken = null
      const query = {
        ...this.$route.query
      }
      if (this.search) {
        query.q = this.search.replace(/\./g, '')
      }
      this.$store.dispatch('suppliers/LIST', {
        resource: 'suppliers',
        query
      })
      .then((response) => {
        const allSuppliers = response?.data?.results ?? response?.data ?? []
        // Filtrar solo proveedores con la categoría por defecto (sin categoría asignada)
        const filteredSuppliers = allSuppliers.filter(supplier =>
          !supplier.segment_id || supplier.segment_id === this.defaultSegmentId
        )
        this.$store.state.suppliers.suppliersList = filteredSuppliers
        this.nextToken = response.headers?.['x-ax-next-token'] || null
      })
      .finally(() => {
        this.loading = false
      })
    },
    onLoadMore (entries, observer, isIntersecting) {
      if (isIntersecting && this.nextToken) {
        this.loadMore()
      }
    },
    loadMore () {
      if (!this.nextToken) return
      const query = {
        ...this.$route.query,
        token: this.nextToken
      }
      if (this.search) {
        query.q = this.search.replace(/\./g, '')
      }
      this.$store.dispatch('suppliers/LIST', {
        resource: 'suppliers',
        query
      })
      .then((response) => {
        const newSuppliers = response?.data?.results ?? response?.data ?? []
        // Filtrar solo proveedores con la categoría por defecto (sin categoría asignada)
        const filteredSuppliers = newSuppliers.filter(supplier =>
          !supplier.segment_id || supplier.segment_id === this.defaultSegmentId
        )
        this.$store.state.suppliers.suppliersList = [
          ...this.suppliersList,
          ...filteredSuppliers
        ]
        this.nextToken = response.headers?.['x-ax-next-token'] || null
      })
    },
    handleRowLeave (item) {
      this.hoveredRow = null
      // Resetear el estado de confirmar si el mouse sale de la fila
      if (this.confirmAdd === item.id) {
        this.confirmAdd = null
      }
    },
    getButtonColor (itemId) {
      if (this.successAdd === itemId) return 'green'
      if (this.confirmAdd === itemId) return 'blue-500'
      return 'blue-500'
    },
    getButtonText (itemId) {
      if (this.successAdd === itemId) return '¡Listo!'
      if (this.confirmAdd === itemId) return 'Confirmar'
      return 'Agregar proveedor'
    },
    handleButtonClick (item) {
      if (this.confirmAdd === item.id) {
        this.addSupplierToCategory(item)
      } else {
        this.confirmAdd = item.id
      }
    },
    addSupplierToCategory (item) {
      this.loadingAdd = item.id
      this.confirmAdd = null
      this.$store.dispatch('suppliers/UPDATE', {
        resource: `suppliers/${item.id}/replace-segment`,
        payload: {
          old_segment_id: this.defaultSegmentId,
          new_segment_id: this.$route.params.id
        }
      })
      .then(() => {
        this.loadingAdd = null
        this.successAdd = item.id
        this.snackbarSupplier = item
        this.showSnackbar = true

        // Remover el proveedor después de 3 segundos (tiempo del snackbar)
        this.snackbarTimeout = setTimeout(() => {
          this.removeSupplierFromList(item.id)
          this.successAdd = null
        }, 3000)
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
        this.loadingAdd = null
        this.confirmAdd = null
      })
    },
    removeSupplierFromList (supplierId) {
      const index = this.suppliersList.findIndex(s => s.id === supplierId)
      if (index !== -1) {
        this.$store.state.suppliers.suppliersList.splice(index, 1)
      }
    },
    handleUndo () {
      if (!this.snackbarSupplier) return

      // Cancelar el timeout de remover
      if (this.snackbarTimeout) {
        clearTimeout(this.snackbarTimeout)
        this.snackbarTimeout = null
      }

      const supplier = this.snackbarSupplier

      // Revertir: volver a asignar al segmento por defecto
      this.$store.dispatch('suppliers/UPDATE', {
        resource: `suppliers/${supplier.id}/replace-segment`,
        payload: {
          old_segment_id: this.$route.params.id,
          new_segment_id: this.defaultSegmentId
        }
      })
      .then(() => {
        this.showSnackbar = false
        this.successAdd = null
        this.snackbarSupplier = null
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
    }
  }
}
</script>
